/**
 * Buddies View
 * Manage automated fishing buddies
 */

import { useState, useEffect } from 'react';
import { usePlayerState } from '@/state';
import { buddyManager, mentorManager } from '@/systems';
import { eventBus } from '@/services/EventBus';
import { GameEvent } from '@/types';
import { getZoneDefinition } from '@/data/zones/zoneData';
import { BuddySlot } from './BuddySlot';
import { BuddyHireCard } from './BuddyHireCard';
import { HaulerSlot } from './HaulerSlot';
import { HaulerHireCard } from './HaulerHireCard';
import { ScrollableView } from '@/components/layout/ScrollableView';

export function BuddiesView() {
  const playerState = usePlayerState();
  const zone = getZoneDefinition(playerState.currentZone);
  const hiredBuddies = playerState.fishingBuddies.filter((b) => b.hired);
  const hiredHaulers = playerState.haulerBuddies.filter((h) => h.hired);

  // Track mentor bonus status via events
  const [mentorActive, setMentorActive] = useState(mentorManager.isActive());

  useEffect(() => {
    const unsub1 = eventBus.on(GameEvent.MENTOR_BONUS_ACTIVATED, () => setMentorActive(true));
    const unsub2 = eventBus.on(GameEvent.MENTOR_BONUS_DEACTIVATED, () => setMentorActive(false));
    return () => { unsub1(); unsub2(); };
  }, []);

  // Generate all possible slots (1 to maxBuddies)
  const allSlots = Array.from({ length: zone.maxBuddies }, (_, i) => i + 1);

  // Hauler slots (always 2)
  const haulerSlots = [1, 2];

  const handleHireBuddy = (slotNumber: number) => {
    buddyManager.hireBuddy(slotNumber);
  };

  const handleToggleBuddy = (slotNumber: number) => {
    buddyManager.toggleBuddy(slotNumber);
  };

  return (
    <ScrollableView gap={0}>
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-white">Fishing Buddies</h2>
        <p className="text-gray-400">
          Hire helpers to automatically fish for you
        </p>
        <p className="text-sm text-gray-500 mt-1">
          {hiredBuddies.length} / {zone.maxBuddies} buddies hired in {zone.name}
        </p>
      </div>

      {/* Mentor Bonus indicator */}
      {mentorActive && (
        <div className="bg-yellow-900/50 border border-yellow-600 rounded-lg px-4 py-2 mb-4 text-sm text-yellow-200">
          <strong>Mentor Bonus Active!</strong> Buddies get +25% catch rate and +5% quality while you're fishing.
        </div>
      )}

      {/* Buddy slots grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {allSlots.map((slotNumber) => {
          const buddy = playerState.fishingBuddies.find(
            (b) => b.slotNumber === slotNumber
          );

          if (buddy?.hired) {
            // Show hired buddy
            return (
              <BuddySlot
                key={slotNumber}
                buddy={buddy}
                onToggle={() => handleToggleBuddy(slotNumber)}
              />
            );
          } else {
            // Show hire card
            const cost = buddyManager.getBuddyCost(slotNumber);
            return (
              <BuddyHireCard
                key={slotNumber}
                slotNumber={slotNumber}
                cost={cost}
                onHire={() => handleHireBuddy(slotNumber)}
              />
            );
          }
        })}
      </div>

      {/* Hauler Buddies Section */}
      <div className="mt-12">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-white">🚚 Hauler Buddies</h2>
          <p className="text-gray-400">
            Haulers automatically take fish from your inventory and sell them at the shop
          </p>
          <p className="text-sm text-gray-500 mt-1">
            {hiredHaulers.length} / 2 haulers hired
          </p>
        </div>

        {/* Hauler slots grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {haulerSlots.map((slotNumber) => {
            const hauler = playerState.haulerBuddies.find(
              (h) => h.slotNumber === slotNumber
            );

            if (hauler?.hired) {
              // Show hired hauler
              return <HaulerSlot key={slotNumber} hauler={hauler} />;
            } else {
              // Show hire card
              return <HaulerHireCard key={slotNumber} slotNumber={slotNumber} />;
            }
          })}
        </div>
      </div>

      {/* Info section */}
      <div className="mt-8 p-4 bg-blue-900/20 border border-blue-500/30 rounded-lg">
        <h3 className="font-bold text-blue-400 mb-2">💡 Buddy Tips</h3>
        <ul className="text-sm text-gray-300 space-y-1">
          <li>• Fishing buddies automatically catch fish every 6 seconds</li>
          <li>• Haulers automatically sell fish from your inventory</li>
          <li>• Both buddy types can be leveled up and upgraded with stat points</li>
          <li>• Buddies pause when inventory is full (fishing) or empty (haulers)</li>
        </ul>
      </div>
    </ScrollableView>
  );
}
